import Flickity from 'flickity';

import resolution from '../util/resolution';

import initSectionModels from './model';
import productNativeVideo from './native-video-player';
import embedYoutube from './youtube-player';
import embedVimeo from './vimeo-player';

const selectors = {
  productSlideshow: '[data-product-slideshow]',
  slideshowMobileStyle: 'data-slideshow-mobile-style',
  slideshowDesktopStyle: 'data-slideshow-desktop-style',
  productThumbs: '[data-product-thumbs]',
  leftThumbsHolder: '[data-thumbs-holder]',
  thumbImage: '[data-slideshow-thumbnail]',
  mediaSlide: '[data-media-slide]',
  dataMediaId: 'data-media-id',
  dataMediaSelect: 'data-media-select',
  mediaType: 'data-type',
  videoPlayerExternal: '[data-type="external_video"]',
  videoPlayerNative: '[data-type="video"]',
  modelViewer: '[data-type="model"]',
  modelViewerTag: 'model-viewer',
  allPlayers: '[data-player]',
  loopVideo: 'data-enable-video-looping',
  verticalAlignment: '[data-thumbnails-left]',
  arrowPrev: '[data-thumbs-arrow-prev]',
  arrowNext: '[data-thumbs-arrow-next]',
  aspectRatio: 'data-aspect-ratio',
  flickitylockHeight: 'flickity-lock-height',
};

const classes = {
  flickityDisableClass: 'flickity-disabled-mobile',
  flickityEnabled: 'flickity-enabled',
  selected: 'is-selected',
  active: 'is-activated',
  show: 'show',
  enableVideoDraggable: 'enable-video-draggable',
  hide: 'hide',
};

class Media {
  constructor(section) {
    this.section = section;
    this.container = section.container;
    this.slideshow = this.container.querySelector(selectors.productSlideshow);
    this.mobileStyle = this.slideshow.getAttribute(selectors.slideshowMobileStyle);
    this.desktopStyle = this.slideshow.getAttribute(selectors.slideshowDesktopStyle);
    this.arrowPrev = this.container.querySelector(selectors.arrowPrev);
    this.arrowNext = this.container.querySelector(selectors.arrowNext);
    this.leftThumbsHolder = this.container.querySelector(selectors.leftThumbsHolder);
    this.thumbWrapper = this.container.querySelector(selectors.productThumbs);
    this.thumbImages = this.container.querySelectorAll(selectors.thumbImage);
    this.loopVideo = this.container.getAttribute(selectors.loopVideo) === 'true';
    this.verticalAlignment = Boolean(this.container.querySelector(selectors.verticalAlignment));

    this.flkty = null;
    this.lastMediaSelect = null;
    this.thumbs = this.container.querySelector(selectors.productThumbs);
    this.currentSlide = null;
    this.mediaType = null;
    this.hasMobileSlider = false;
    this.hasDesktopSlider = false;

    this.init();
  }

  init() {
    this.storeEvents();
    this.connectSliderMediaEvents();
    this.detectVideos();
    this.detect3d();
    this.scrollThumbs();

    document.addEventListener('theme:resize:width', () => {
      this.scrollThumbs();
    });

    resolution.onChange(() => {
      this.connectSliderMediaEvents();
    });
  }

  connectSliderMediaEvents() {
    resolution.isMobile() ? this.createMobileSlider() : this.createDesktopSlider();
  }

  createMobileSlider() {
    if (this.hasDesktopSlider && this.flkty) {
      this.destroySlider();
      this.hasDesktopSlider = false;
    }

    // defaults to "thumbs" option
    const mobileOptions = {
      autoPlay: false,
      prevNextButtons: false,
      pageDots: false,
      adaptiveHeight: true,
      accessibility: true,
      watchCSS: false,
      wrapAround: true,
      rightToLeft: window.isRTL,
      dragThreshold: 80,
      contain: true,
      fade: true,
    };
    if (this.mobileStyle == 'carousel') {
      mobileOptions.contain = false;
      mobileOptions.dragThreshold = 10;
      mobileOptions.fade = false;
    }
    if (this.mobileStyle == 'slideshow') {
      mobileOptions.pageDots = true;
      mobileOptions.fade = false;
      mobileOptions.dragThreshold = 10;
    }
    if (this.mobileStyle == 'none') return;

    this.createSlider(mobileOptions);
    this.hasMobileSlider = true;
  }

  createDesktopSlider() {
    if (this.hasMobileSlider && this.flkty) {
      this.destroySlider();
      this.hasMobileSlider = false;
    }
    if (this.desktopStyle == 'none') {
      return;
    }
    const desktopOptions = {
      autoPlay: false,
      prevNextButtons: false,
      pageDots: false,
      adaptiveHeight: true,
      accessibility: true,
      watchCSS: false,
      wrapAround: true,
      rightToLeft: window.isRTL,
      dragThreshold: 80,
      contain: true,
      fade: true,
    };
    this.createSlider(desktopOptions);
    this.hasDesktopSlider = true;
  }

  createSlider(options) {
    this.flkty = new Flickity(this.slideshow, options);

    this.flkty.resize();

    this.currentSlide = this.slideshow.querySelectorAll(selectors.mediaSlide)[0];

    this.setDraggable();
    this.addEventListeners();
  }

  destroySlider() {
    this.removeEventListeners();
    this.flkty.destroy();
  }

  storeEvents() {
    this.storeFlktyChange = (e) => this.doFlktyChange(e);
    this.storeFlktySettle = (e) => this.doFlktySettle(e);
    this.storeImageChange = (e) => this.doImageChange(e);
    this.storeArrowPrevClick = (e) => this.doArrowPrevClick(e);
    this.storeArrowNextClick = (e) => this.doArrowNextClick(e);
    this.storeThumbClick = (e) => this.doThumbClick(e);
    this.storeThumbContainerScroll = (e) => this.doThumbContainerScroll(e);
  }

  addEventListeners() {
    this.flkty.on('change', this.storeFlktyChange);
    this.flkty.on('settle', this.storeFlktySettle);

    this.slideshow.addEventListener('theme:image:change', this.storeImageChange);

    this.arrowPrev?.addEventListener('click', this.storeArrowPrevClick);
    this.arrowNext?.addEventListener('click', this.storeArrowNextClick);

    this.thumbs?.addEventListener('scroll', this.storeThumbContainerScroll);
    this.thumbImages.forEach((thumb) => {
      thumb.addEventListener('click', this.storeThumbClick);
    });
  }

  removeEventListeners() {
    this.flkty.on('change', this.storeFlktyChange);
    this.flkty.on('settle', this.storeFlktySettle);

    this.slideshow.removeEventListener('theme:image:change', this.storeImageChange);

    this.arrowPrev?.removeEventListener('click', this.storeArrowPrevClick);
    this.arrowNext?.removeEventListener('click', this.storeArrowNextClick);

    this.thumbs?.removeEventListener('scroll', this.storeThumbContainerScroll);
    this.thumbImages.forEach((thumb) => {
      thumb.removeEventListener('click', this.storeThumbClick);
    });
  }

  addMediaEventListeners(media) {
    if (!media) return;

    media.addEventListener('play', () => {
      if (resolution.isMobile() || resolution.isTouch()) {
        this.updateDraggable(false);
      }
    });

    media.addEventListener('pause', () => {
      if (resolution.isMobile() || resolution.isTouch()) {
        this.updateDraggable(true);
      }
    });
  }

  doFlktyChange(index) {
    // Pause previous slide media
    if (this.mediaType === 'model' || this.mediaType === 'video' || this.mediaType === 'external_video') {
      this.currentSlide.dispatchEvent(new CustomEvent('pause'));
    }

    this.currentSlide = this.flkty.cells[index].element;
    this.slideshow.classList.remove(selectors.flickitylockHeight);
    const id = this.currentSlide.getAttribute(selectors.dataMediaId);
    const currentThumb = this.thumbWrapper?.querySelector(`[${selectors.dataMediaSelect}="${id}"]`);

    this.thumbWrapper?.querySelector(`.${classes.active}`).classList.remove(classes.active);
    currentThumb?.classList.add(classes.active);

    this.scrollThumbs();

    // when swatch images are hidden by specific alt-text, the slider breaks on change so we select the featured variant image to "fix" it
    // Limitation: images must be in order in the media slideshow to use the alt text hack
    // Timeout is needed to slow it down a bit until the images refresh and the currentThumb variable has the right value
    setTimeout(() => {
      if (currentThumb?.classList.contains(classes.hide) || this.currentSlide.classList.contains(classes.hide)) {
        if (this.lastMediaSelect) {
          this.slideshow.dispatchEvent(
            new CustomEvent('theme:image:change', {
              detail: {
                id: this.lastMediaSelect,
              },
            })
          );
        } else window.location.reload();
      }
    });
  }

  doFlktySettle(index) {
    this.allSlides = this.flkty.cells;
    this.currentSlide = this.flkty.cells[index].element;

    this.setDraggable();

    const isFocusEnabled = document.body.classList.contains(selectors.focusEnabled);
    if (isFocusEnabled) this.currentSlide.dispatchEvent(new Event('focus'));
    this.scrollThumbs();
  }

  doImageChange(event) {
    var mediaId = event.detail.id;
    this.lastMediaSelect = mediaId;
    const mediaIdString = `[${selectors.dataMediaId}="${mediaId}"]`;

    const matchesMedia = (cell) => {
      return cell.element.matches(mediaIdString);
    };
    const index = this.flkty.cells.findIndex(matchesMedia);

    this.flkty.select(index);
    this.scrollThumbs();
  }

  doThumbContainerScroll() {
    this.scrollThumbs('scroll');
  }

  doArrowPrevClick() {
    this.scrollThumbs('clickPrev');
  }

  doArrowNextClick() {
    this.scrollThumbs('clickNext');
  }

  doThumbClick(event) {
    const id = event.currentTarget.getAttribute(selectors.dataMediaSelect);
    this.slideshow.dispatchEvent(
      new CustomEvent('theme:image:change', {
        detail: {
          id: id,
        },
      })
    );
  }

  setDraggable() {
    if (this.currentSlide) {
      this.mediaType = this.currentSlide.getAttribute(selectors.mediaType);

      if (this.mediaType === 'model' || this.mediaType === 'video' || this.mediaType === 'external_video') {
        if (!resolution.isMobile() || !resolution.isTouch()) {
          // Play on Desktop
          this.currentSlide.dispatchEvent(new CustomEvent('play'));
          this.updateDraggable(false);
        } else {
          this.updateDraggable(true); // Triggering this to ensure the first video also has the 'draggable' elements active on Mobile
        }
      } else {
        this.updateDraggable(true);
      }
    }
  }

  updateDraggable(state) {
    if (!this.flkty) return;

    this.flkty.options.draggable = state;
    this.flkty.updateDraggable();
    this.slideshow.classList.toggle(classes.enableVideoDraggable, state);
  }

  detect3d() {
    const modelViewerElements = this.container.querySelectorAll(selectors.modelViewer);

    if (modelViewerElements) {
      modelViewerElements.forEach((element) => {
        initSectionModels(element, this.section.id);

        element.addEventListener('theme:media:play', () => {
          this.updateDraggable(false);
        });

        element.addEventListener('theme:media:pause', () => {
          this.updateDraggable(true);
        });
      });

      document.addEventListener(
        'shopify_xr_launch',
        function () {
          this.container.querySelectorAll(selectors.allPlayers).forEach((player) => {
            player.dispatchEvent(new CustomEvent('pause'));
          });
        }.bind(this)
      );
    }
  }

  detectVideos() {
    const playerElements = this.section.container.querySelectorAll(`${selectors.videoPlayerExternal}, ${selectors.videoPlayerNative}`);

    for (var player of playerElements) {
      const uniqueKey = player.dataset.player;
      const host = player.dataset.host;
      let videoPlayerPromise;

      if (host === 'youtube') {
        // Youtube
        videoPlayerPromise = embedYoutube(uniqueKey);
      } else if (host === 'vimeo') {
        // Vimeo
        videoPlayerPromise = embedVimeo(uniqueKey);
      } else {
        // Native video
        videoPlayerPromise = productNativeVideo(uniqueKey);
      }

      if (this.loopVideo === true) {
        videoPlayerPromise
          .then((videoPlayer) => {
            if (host) {
              // Youtube and Vimeo
              return _setToLoop(videoPlayer, host);
            } else {
              // Native video
              videoPlayer.loop = true;
              return videoPlayer;
            }
          })
          .catch((err) => {
            console.error(err);
          });
      }

      this.addMediaEventListeners(player);
    }
  }

  pauseAllMedia() {
    const all = this.container.querySelector(selectors.mediaSlide);
    all.dispatchEvent(new CustomEvent('pause'));
  }

  pauseOtherMedia(uniqueKey) {
    const otherMedia = this.container.querySelector(`${selectors.mediaSlide}:not([data-player="${uniqueKey}"])`);
    otherMedia.dispatchEvent(new CustomEvent('pause'));
  }

  destroy() {
    this.container.querySelectorAll(selectors.allPlayers).forEach((player) => {
      player.dispatchEvent(new CustomEvent('destroy'));
    });
  }

  scrollThumbs(event) {
    this.thumbs = this.container.querySelector(selectors.productThumbs);

    if (this.thumbs) {
      this.thumb = this.thumbs.querySelector(`.${classes.active}`);
      this.thumbItems = this.container.querySelectorAll(selectors.thumbImage);
      this.lastThumb = this.thumbItems[this.thumbItems.length - 1];

      if (!this.thumb) return;
      this.thumbsScrollTop = this.thumbs.scrollTop;
      this.thumbsScrollLeft = this.thumbs.scrollLeft;
      this.thumbsWidth = this.thumbs.offsetWidth;
      this.thumbsHeight = this.slideshow.offsetWidth / this.thumb.getAttribute(selectors.aspectRatio);
      this.thumbsPositionBottom = this.thumbsScrollTop + this.thumbsHeight;
      this.thumbsPositionRight = this.thumbsScrollLeft + this.thumbsWidth;

      this.checkThumbPositions();
      this.toggleToActiveThumb(event);
      this.toggleArrows();
    }
  }

  checkThumbPositions() {
    this.thumbWidth = this.thumb.offsetWidth;
    this.thumbHeight = this.thumb.offsetHeight;
    this.thumbPosTop = this.thumb.offsetTop;
    this.thumbPosLeft = this.thumb.offsetLeft;
    this.lastThumbRightPos = this.lastThumb.offsetLeft + this.thumbWidth;
    this.lastThumbBottomPos = this.lastThumb.offsetTop + this.thumbHeight;
    this.scrollTopPosition = this.thumbPosTop + this.thumbHeight / 2 - this.thumbsHeight / 2;
    this.scrollLeftPosition = this.thumbPosLeft + this.thumbWidth / 2 - this.thumbsWidth / 2;

    this.topCheck = this.thumbsScrollTop > 0;
    this.bottomCheck = this.thumbsPositionBottom < this.lastThumbBottomPos;
    this.leftCheck = this.thumbsScrollLeft > 0;
    this.rightCheck = this.thumbsPositionRight < this.lastThumbRightPos;
    this.verticalCheck = this.bottomCheck || this.topCheck;
    this.horizontalCheck = this.rightCheck || this.leftCheck;
  }

  toggleToActiveThumb(event) {
    if (event !== 'scroll') {
      if (event == 'clickPrev') {
        if (this.verticalCheck) {
          this.scrollTopPosition = this.thumbsScrollTop - this.thumbsHeight;
          this.scrollLeftPosition = 0;
        } else {
          this.scrollTopPosition = 0;
          this.scrollLeftPosition = this.thumbsScrollLeft - this.thumbsWidth;
        }
      }
      if (event == 'clickNext') {
        if (this.verticalCheck) {
          this.scrollTopPosition = this.thumbsScrollTop + this.thumbsHeight;
          this.scrollLeftPosition = 0;
        } else {
          this.scrollTopPosition = 0;
          this.scrollLeftPosition = this.thumbsScrollLeft + this.thumbsWidth;
        }
      }

      this.thumbs.scrollTo({
        top: this.scrollTopPosition,
        left: this.scrollLeftPosition,
        behavior: 'smooth',
      });
    }
  }

  toggleArrows() {
    if (this.verticalCheck || (this.verticalAlignment && !this.verticalCheck)) {
      this.arrowPrev.classList.toggle(classes.show, this.topCheck);
      this.arrowNext.classList.toggle(classes.show, this.bottomCheck);
    }

    if (this.horizontalCheck || (!this.verticalAlignment && !this.horizontalCheck)) {
      this.arrowPrev.classList.toggle(classes.show, this.leftCheck);
      this.arrowNext.classList.toggle(classes.show, this.rightCheck);
    }
  }
}

function _setToLoop(player, host) {
  if (host === 'youtube') {
    player.addEventListener('onStateChange', (event) => {
      if (event.data === 0) {
        // video is over, replay
        event.target.playVideo();
      }
    });
  } else if (host === 'vimeo') {
    player.on('ended', () => {
      // video is over, replay
      player.play();
    });
  }

  return player;
}

export default Media;
